import axios from "axios";
import { API_URL } from "../constants";

export interface CallsConfig {
  /** false when this deployment has no calls service configured */
  enabled: boolean;
  /** where to load the call engine from — it is served by the calls service,
   *  not bundled here, so there is exactly one copy of it in existence */
  sdkUrl: string | null;
}

export interface CallTokenResponse {
  token: string;
  expiresAt: number;
  /** where the browser opens the calls socket (the 676767 service) */
  url: string;
  self: { userId: string; name: string };
  /** null when joining a group call we were invited into: there is no single
   *  other side then, the roster says who is there */
  peer: { userId: string; name: string } | null;
}

export async function fetchCallsConfig(): Promise<CallsConfig> {
  try {
    const res = await axios.get(`${API_URL}/me/calls-config`);
    return {
      enabled: !!res.data?.enabled,
      sdkUrl: res.data?.sdkUrl ?? null,
    };
  } catch {
    return { enabled: false, sdkUrl: null };
  }
}

export async function fetchCallToken(
  companyId: string,
  conversationId: string,
  callId: string
): Promise<CallTokenResponse> {
  const res = await axios.post(
    `${API_URL}/${companyId}/conversations/${conversationId}/call-token`,
    { callId }
  );
  return res.data as CallTokenResponse;
}

export type CallSignalType = "ring" | "cancel" | "decline" | "end" | "reload";

export interface CallSignalBody {
  type: CallSignalType;
  callId: string;
  kind?: "audio" | "video";
  reason?: "busy";
  /** who to aim it at — needed once a call has more than two people in it */
  to?: string;
}

/**
 * Ring / cancel / decline / end. These ride the ERP socket rather than the
 * calls service: the callee has to hear the phone while merely logged in, long
 * before their browser joins the call room.
 */
export async function sendCallSignal(
  companyId: string,
  conversationId: string,
  body: CallSignalBody
): Promise<{ delivered: boolean }> {
  const res = await axios.post(
    `${API_URL}/${companyId}/conversations/${conversationId}/call-signal`,
    body
  );
  return res.data as { delivered: boolean };
}

/**
 * Same signal, sent from a page that is going away. axios cannot outlive the
 * document; `keepalive` lets the browser finish the request after the tab is
 * gone, which is the only way a reload during a ring tells the other side to
 * stop ringing. Best-effort by nature — the receiving side still has its own
 * timeout as the real guarantee.
 */
export function sendCallSignalBeacon(
  companyId: string,
  conversationId: string,
  body: CallSignalBody
): void {
  try {
    void fetch(
      `${API_URL}/${companyId}/conversations/${conversationId}/call-signal`,
      {
        method: "POST",
        credentials: "include",
        keepalive: true,
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }
    ).catch(() => {});
  } catch {
    /* the page is unloading; nothing to recover */
  }
}

/** Pull a colleague into the call that is already running. They get a ring of
 *  their own; accepting puts them in the same room. */
export async function inviteToCall(
  companyId: string,
  conversationId: string,
  body: { callId: string; userId: string }
): Promise<{ delivered: boolean; throttled?: boolean }> {
  const res = await axios.post(
    `${API_URL}/${companyId}/conversations/${conversationId}/call-invite`,
    body
  );
  return res.data as { delivered: boolean; throttled?: boolean };
}

export interface GuestLink {
  token: string;
  expiresAt: number;
  /** ready-made URL when the backend knows where we live; otherwise build it */
  url: string | null;
  path: string;
}

/** Cut a link that lets someone without an ERP account into THIS call. Only
 *  works from inside the call, and only while it is running. */
export async function createGuestLink(
  companyId: string,
  conversationId: string,
  body: { callId: string }
): Promise<GuestLink> {
  const res = await axios.post(
    `${API_URL}/${companyId}/conversations/${conversationId}/call-guest-link`,
    body
  );
  return res.data as GuestLink;
}

export interface GuestJoin {
  token: string;
  expiresAt: number;
  url: string;
  /** where the call engine is served from — a guest has no ERP session to ask */
  sdkUrl: string;
  self: { userId: string; name: string };
}

/** The guest side of that link. No session, no cookie: the link is the whole
 *  authorization, and it reaches nothing but this one call. */
export async function joinCallAsGuest(
  token: string,
  name: string
): Promise<GuestJoin> {
  const res = await axios.post(`${API_URL}/call-guest/join`, { token, name });
  return res.data as GuestJoin;
}
